import authenticator from 'authenticator';
// Safe to ignore: ajax functions are only called at runtime, not during module initialization
import * as ajax from './ajax'; // eslint-disable-line import/no-cycle
import mfaConstants from '../constants/mfaConstants';
import loginActions from './loginActionCreators';
import tokenErrorActions from './tokenErrorActionCreators';
import notificationActions from './notificationActionCreators';
const loadSecret = () => ({
  type: mfaConstants.MFA_LOAD_SECRET,
  secret: authenticator.generateKey()
});
const saveSuccess = pin => ({
  type: mfaConstants.MFA_SAVE_SUCCESS,
  data: pin
});
const saveFailure = err => ({
  type: mfaConstants.MFA_SAVE_FAIL,
  data: err
});
const deleteSuccess = () => ({
  type: mfaConstants.MFA_DESTROY_SUCCESS
});
const deleteFailure = () => ({
  type: mfaConstants.MFA_DESTROY_FAIL
});
const clearError = () => ({
  type: mfaConstants.MFA_CLEAR_ERROR
});
const mfaLoginClear = () => ({
  type: mfaConstants.MFA_LOGIN_CLEAR
});

/**
 * Toggles a modal on and off
 * @param {String} modalType - The modal we want to toggle
 */
const toggleModal = modalType => ({
  type: mfaConstants.MFA_MODAL_TOGGLE,
  modalType
});

/**
 * This will be triggered when we recieve a MFA setup request
 * from the server.  We store the user credentials so we can automatically
 * log them in once they have setup MFA.
 * @param {String} customer - The customer the user is associated with
 * @param {String} username - The username of the user
 * @param {String} password - The password provided by the user
 */
const startMFASetup = (customer, username, password) => dispatch => {
  dispatch({
    type: mfaConstants.MFA_LOGIN_SAVE,
    customer,
    username,
    password
  });
};

/**
 * Clear the user credentials from the store AND dispatch a login event
 * so the user logs in automatically.
 * @param {String} pin - The pin that can be used to log the user in
 */
const finishMFASetup = pin => (dispatch, getState) => {
  const {
    username,
    password,
    customer
  } = getState().mfa.login;
  dispatch(loginActions.serverLogin(customer, username, password, pin));
  dispatch(mfaLoginClear());
};

/**
 * Attempts to verify that both pins were generated for the
 * provided secret
 * @param {String} secret - Generated secret for MFA
 * @param {String} firstPin - First pin the user entered
 * @param {String} secondPin - Second pin the user entered
 */
const validateSecret = (secret, firstPin, secondPin) => {
  if (!firstPin.length || !secondPin.length) {
    return {
      error: 'Please provide two consecuative pins.'
    };
  }
  const authPin1 = authenticator.verifyToken(secret, firstPin);
  const authPin2 = authenticator.verifyToken(secret, secondPin);
  let areDifferentPins = false;
  if (authPin1 && authPin2) {
    areDifferentPins = authPin1.delta !== authPin2.delta;
  }
  if (!areDifferentPins) {
    return {
      error: 'A pin is incorrect.  Please provide two different, consecuative, pins.'
    };
  }
  return {
    authPin: secondPin
  };
};

/**
 * Verify that the pins provided are correct and if they are, save the secret
 * to the server.
 * @param {String} id - ID of user to save
 * @param {String} firstPin - First pin for verification
 * @param {String} secondPin - Second pin for verification
 */
const serverSaveSecret = (id, firstPin, secondPin) => async (dispatch, getState) => {
  const {
    secret,
    login
  } = getState().mfa;
  const {
    error,
    authPin
  } = validateSecret(secret, firstPin, secondPin);
  if (error) {
    return dispatch(saveFailure(error));
  }
  const url = `/mfaSecret/${id}`;
  try {
    await ajax.put(url, JSON.stringify({
      secret
    }));
    dispatch(notificationActions.success({
      message: 'Successfully saved MFA settings.',
      id: authPin
    }));
    dispatch(saveSuccess(authPin));
    if (login) {
      /**
       * If MFA was setup before login
       * we need to automatically log the user
       * in.
       */
      return dispatch(finishMFASetup(authPin));
    }
    return null;
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'mfa', saveFailure.bind(this, ajax.getError(e).error_msg), serverSaveSecret.bind(this, id, firstPin, secondPin)));
  }
};

/**
 * Removes MFA from a user
 * @param {String} id - id of user to delete secret for
 */
const serverDeleteSecret = id => async dispatch => {
  try {
    await ajax.remove(`/mfaSecret/${id}`);
    return dispatch(deleteSuccess());
  } catch (e) {
    return dispatch(tokenErrorActions.handleError(e, 'mfa', deleteFailure.bind(this, ajax.getError(e)), serverDeleteSecret.bind(this, id)));
  }
};
const actions = {
  toggleModal,
  loadSecret,
  serverSaveSecret,
  serverDeleteSecret,
  startMFASetup,
  finishMFASetup,
  clearError
};
export default actions;